import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { activeSession, muscleName } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { planTabs } from "../components/planTabs.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { spinner, errorState, emptyState, toast, confirmDialog, openModal } from "../components/ui.js";
import { openWizard } from "../components/Wizard.js";
import { encodeWorkout, shareUrl } from "../lib/share.js";
import { openExportDoc } from "../components/exportModal.js";
import { workoutToMarkdown } from "../lib/exportDoc.js";

export default function Workouts() {
  const root = h("div", { class: "screen plan-screen" });
  load();

  async function load() {
    setChildren(root, header(), planTabs("workouts"), spinner());
    try {
      const workouts = await api.workouts();
      render(workouts);
    } catch (e) {
      setChildren(root, header(), planTabs("workouts"), errorState(e.message, load));
    }
  }

  function header() {
    return topbar("Plan", { sub: "Templates and programs" });
  }

  function render(workouts) {
    setChildren(root,
      header(),
      planTabs("workouts"),
      h(
        "div",
        { class: "btn-row" },
        h("button", { class: "btn btn-primary grow", onClick: () => navigate("/workouts/new") }, icon("plus", { size: 18 }), "New workout"),
        h("button", { class: "btn btn-ghost grow", onClick: startWizard }, icon("dumbbell", { size: 18 }), "Guided builder"),
        h("button", { class: "btn btn-ghost grow", onClick: () => navigate("/library") }, icon("search", { size: 18 }), "Exercises")
      ),
      workouts.length
        ? h("div", { class: "mt workout-grid" }, ...workouts.map(card))
        : emptyState({ icon: "list", title: "No workouts yet", message: "Create a template or let the wizard build one.", action: h("button", { class: "btn btn-primary", onClick: () => navigate("/workouts/new") }, "Create workout") }),
      h("button", { class: "btn btn-ghost btn-block mt", style: { opacity: ".85" }, onClick: () => navigate("/import") }, icon("download", { size: 18 }), "Import a workout or program")
    );
  }

  function card(w) {
    const primary = w.exercises.flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "120px";

    const exNames = w.exercises.slice(0, 4).map((e) => e.exercise_name).join(" · ") + (w.exercises.length > 4 ? "…" : "");

    return h(
      "div",
      { class: "card", style: { marginBottom: "12px" } },
      h(
        "div",
        { class: "hstack", style: { alignItems: "flex-start" } },
        mini,
        h(
          "div",
          { class: "grow" },
          h("div", { style: { fontWeight: 700, fontSize: "1.1rem" } }, w.name, w.is_public ? h("span", { class: "badge", style: { marginLeft: "8px", verticalAlign: "middle" } }, "Public") : null),
          programBadge(w.active_program),
          h("div", { class: "dim", style: { marginTop: "2px" } },
            `${w.exercises.length} exercises`,
            w.is_public && w.use_count ? ` · ${w.use_count} using` : "",
            w.is_public && w.rating_count ? ` · ★ ${w.avg_rating} (${w.rating_count})` : ""
          ),
          h("div", { class: "dim", style: { marginTop: "4px", fontSize: "0.8rem" } }, exNames),
          w.source_author ? sourceLine(w) : null
        )
      ),
      h(
        "div",
        { class: "btn-row mt" },
        h("button", { class: "btn btn-primary grow", onClick: () => begin(w.id) }, icon("play", { size: 18, fill: true }), "Start"),
        h("button", { class: "btn btn-ghost", "aria-label": `Edit ${w.name}`, onClick: () => navigate(`/workouts/${w.id}/edit`) }, icon("edit", { size: 18 })),
        h("button", { class: "btn btn-ghost", "aria-label": `Options for ${w.name}`, onClick: () => menu(w) }, icon("grip", { size: 18 }))
      )
    );
  }

  function programBadge(program) {
    if (!program?.days?.length) return null;
    const dayLabel = formatProgramDays(program.days);
    return h("div", { style: { marginTop: "5px", minWidth: 0 } },
      h("span", {
        class: "badge badge-program",
        title: `In active program ${program.name}: ${dayLabel}`,
      },
        icon("calendar", { size: 13 }),
        h("span", { class: "program-name" }, program.name),
        h("span", { "aria-hidden": "true" }, "·"),
        h("span", { class: "program-days" }, dayLabel)
      )
    );
  }

  // Origin line for an adopted workout. `source_name` is the title snapshot captured at copy
  // time, so it stays accurate even if the author later renames/repurposes the source. We link
  // to the live original only while it's still public; otherwise it's plain text (no dead links).
  function sourceLine(w) {
    const link = (text) =>
      w.source_available
        ? h("a", { href: `/discover/${w.source_workout_id}`, "data-link": "", style: { color: "var(--accent)" }, onClick: (e) => { e.preventDefault(); navigate(`/discover/${w.source_workout_id}`); } }, text)
        : h("span", null, text);
    const body = w.source_name
      ? h("span", null, "Copied from ", link(w.source_name), " by ", w.source_author)
      : h("span", null, "Copied from ", link(w.source_author));
    return h("div", { class: "dim", style: { marginTop: "4px", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "4px" } },
      icon("copy", { size: 13, className: "icon muted" }), body);
  }

  async function begin(id) {
    if (activeSession()) {
      const ok = await confirmDialog({ title: "Resume or replace?", message: "You already have an active session. Open it instead?", confirmText: "Open active" });
      if (ok) return navigate("/session");
      return;
    }
    try {
      const session = await api.startSession({ workout_id: id });
      activeSession(session);
      navigate("/session");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function menu(w) {
    const m = openModal({
      title: w.name,
      content: h(
        "div",
        null,
        h("button", { class: "btn btn-ghost btn-block", onClick: () => { m.close(); navigate(`/workouts/${w.id}/edit`); } }, icon("edit", { size: 18 }), "Edit"),
        w.is_public
          ? h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); unpublish(w); } }, icon("x", { size: 18 }), "Make private")
          : h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); publish(w); } }, icon("forward", { size: 18 }), "Publish to Discover"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); share(w); } }, icon("forward", { size: 18 }), "Share code"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); exportDoc(w); } }, icon("download", { size: 18 }), "Export document"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); navigate(`/history?workout=${w.id}`); } }, icon("history", { size: 18 }), "History"),
        h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: () => { m.close(); del(w); } }, icon("trash", { size: 18 }), "Delete")
      ),
    });
  }

  function share(w) {
    const code = encodeWorkout(w);
    const url = shareUrl(code);
    const copy = (text, label) => {
      navigator.clipboard?.writeText(text).then(() => toast(`${label} copied`, "success"), () => toast("Copy failed", "error"));
    };
    openModal({
      title: `Share "${w.name}"`,
      content: h(
        "div",
        null,
        h("p", { class: "dim" }, "Anyone with this link or code can add this workout to their account."),
        h("input", { class: "input mt", readonly: true, value: url, onclick: (e) => e.target.select() }),
        h("div", { class: "btn-row mt" },
          h("button", { class: "btn btn-primary grow", onClick: () => copy(url, "Link") }, icon("copy", { size: 18 }), "Copy link"),
          h("button", { class: "btn btn-ghost grow", onClick: () => copy(code, "Code") }, "Copy code"),
          navigator.share ? h("button", { class: "btn btn-ghost", onClick: () => navigator.share({ title: w.name, text: `Check out my "${w.name}" workout`, url }).catch(() => {}) }, icon("forward", { size: 18 })) : null
        )
      ),
    });
  }

  function exportDoc(w) {
    openExportDoc({
      title: w.name,
      exerciseIds: w.exercises.map((e) => e.library_exercise_id),
      build: ({ perf }) => workoutToMarkdown(w, { perf }),
    });
  }

  function publish(w) {
    let description = w.description || "";
    const textarea = h("textarea", { class: "input", rows: 3, placeholder: "Describe your workout (optional)", oninput: (e) => (description = e.target.value) }, description);
    const m = openModal({
      title: `Publish "${w.name}"`,
      content: h("div", null,
        h("p", { class: "dim" }, "Anyone can find this in Discover, see you as the author, rate it, and add it to their workouts."),
        h("div", { class: "field mt" }, h("label", null, "Description"), textarea),
        h("button", { class: "btn btn-primary btn-block mt", onClick: async () => {
          m.close();
          try { await api.publishWorkout(w.id, { description }); toast("Published to Discover", "success"); load(); }
          catch (e) { toast(e.message, "error"); }
        } }, icon("forward", { size: 18 }), "Publish")
      ),
    });
  }

  async function unpublish(w) {
    try { await api.unpublishWorkout(w.id); toast("Made private", "success"); load(); }
    catch (e) { toast(e.message, "error"); }
  }

  async function del(w) {
    const ok = await confirmDialog({ title: "Delete workout?", message: `"${w.name}" will be removed. Past sessions are kept.`, confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteWorkout(w.id);
      toast("Workout deleted", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function startWizard() {
    // Wizard hands selected exercises to a fresh editor via sessionStorage.
    openWizard({
      onComplete: (data) => {
        sessionStorage.setItem("wizard_prefill", JSON.stringify(data));
        navigate("/workouts/new");
      },
    });
  }

  return root;
}

export function formatProgramDays(days) {
  const valid = [...new Set((days || []).map(Number).filter((day) => Number.isInteger(day) && day > 0))].sort((a, b) => a - b);
  if (!valid.length) return "";
  if (valid.length === 1) return `Day ${valid[0]}`;
  const joined = valid.length === 2
    ? `${valid[0]} & ${valid[1]}`
    : `${valid.slice(0, -1).join(", ")} & ${valid[valid.length - 1]}`;
  return `Days ${joined}`;
}
